import { useCallback, useEffect, useMemo } from 'react';
import { useEnterpriseInquiryFilters } from './use-enterprise-inquiry-filters';
import { useEnterpriseInquiryModals } from './use-enterprise-inquiry-modals';
import { useEnterpriseInquiryPagination } from './use-enterprise-inquiry-pagination';
import { usePlanInquiryList } from './queries';
import type { PlanInquiryStatus } from '@uniquegood/realworld-admin-interface';

/**
 * 엔터프라이즈 문의 페이지 전역 컨트롤러
 *
 * @description
 * - 필터, 페이지네이션, 모달 상태를 한 곳에서 관리합니다.
 * - planInquiryApi.getPlanInquiries를 사용하여 실제 API를 호출합니다.
 */
export function useEnterpriseInquiryController() {
  const filters = useEnterpriseInquiryFilters();
  const modals = useEnterpriseInquiryModals();
  const {
    currentPage,
    limit,
    totalPages,
    setTotal,
    goToPage,
    handleNextPage,
    handlePrevPage,
    resetPagination,
  } = useEnterpriseInquiryPagination({
    initialLimit: 10,
  });

  const inquiryQuery = usePlanInquiryList({
    page: currentPage,
    limit,
    searchTerm: filters.searchTerm || undefined,
    statusFilter: filters.statusFilter === 'all' ? undefined : (filters.statusFilter as PlanInquiryStatus),
    managerFilter: filters.managerFilter === 'all' || filters.managerFilter === 'none' 
      ? undefined 
      : filters.managerFilter,
    isNotAssigned: filters.managerFilter === 'none' ? true : undefined,
  });

  const inquiries = useMemo(() => inquiryQuery.data?.inquiries ?? [], [inquiryQuery.data?.inquiries]);

  useEffect(() => {
    setTotal(inquiryQuery.data?.total ?? 0);
  }, [inquiryQuery.data?.total, setTotal]);

  const handleSearch = useCallback(() => {
    filters.executeSearch();
    resetPagination();
  }, [filters, resetPagination]);

  const handleStatusFilterChange = useCallback(
    (status: typeof filters.statusFilter) => {
      filters.setStatusFilter(status);
      resetPagination();
    },
    [filters, resetPagination]
  );

  const handleManagerFilterChange = useCallback(
    (manager: typeof filters.managerFilter) => {
      filters.setManagerFilter(manager);
      resetPagination();
    },
    [filters, resetPagination]
  );

  return {
    // 데이터
    inquiries,
    isLoading: inquiryQuery.isLoading,
    isFetching: inquiryQuery.isFetching,
    error: inquiryQuery.error,

    // 필터
    searchInput: filters.searchInput,
    searchTerm: filters.searchTerm,
    statusFilter: filters.statusFilter,
    managerFilter: filters.managerFilter,
    setSearchInput: filters.setSearchInput,
    setStatusFilter: handleStatusFilterChange,
    setManagerFilter: handleManagerFilterChange,

    // 페이지네이션
    currentPage,
    totalPages,
    goToPage,
    handleNextPage,
    handlePrevPage,

    // 검색
    handleSearch,

    // 모달 액션
    openDetailModal: modals.openDetailModal,
  };
}

